import { useEffect, useState } from 'react'
import {
  EntityScreenRow,
  entityParentLabel,
  entityProfileLabel,
  entityTypeLabel,
} from 'app/models/entity-screen-row'
import { entityCols, EntityColsTableDefs, StatusState } from './entities-table-columnDefs'
import { Stack, Group } from '@mantine/core'
import { useDispatch, useSelector } from 'react-redux'
import {
  getEntityScreenLoader,
  getEntityScreenRows,
  isEntityScreenStale,
} from 'app/state/ducks/entity-screen/selectors'
import { loadEntityScreen } from 'app/state/ducks/entity-screen/thunks'
import { setEntityScreenStatuses } from 'app/state/ducks/entity-screen/actions'
import { useNavigate } from 'react-router-dom'
import MainTable from 'app/views/components/Table/MainTable'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import BulkActionsButton from 'app/views/components/buttons/bulk-actions-button'
import { getKPIData } from './entity-kpi-data'
import { activateBulkEntities, deactivateBulkEntities } from 'app/state/ducks/entities/thunks'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { GridSortingStorage, GridFilterStorage, GridPaginationStorage, GridColumnVisibilityStorage, useGridSorting, useGridFilters, useGridPagination, useGridColumnVisibility } from 'app/utils/grid-sorting-storage'


export function ManageEntities() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const screenLoading = useSelector(getEntityScreenLoader)
  const stale = useSelector(isEntityScreenStale)
  const [bulkRunning, setBulkRunning] = useState(false)
  const globalLoading = screenLoading || bulkRunning
  const entities: EntityScreenRow[] = useSelector(getEntityScreenRows)
  const [filteredEntities, setFilteredEntities] = useState<EntityScreenRow[]>(entities)
  const [selectedBulkEntities, setSelectedBulkEntities] = useState<EntityScreenRow[]>([])
  const [tableSelectedRows, setTableSelectedRows] = useState<Record<string, boolean>>({});

  // Grid sorting with localStorage persistence
  const { sorting, handleSortingChange } = useGridSorting(GridSortingStorage.entities)

  // Grid filters with localStorage persistence
  const { toMRTColumnFilters, handleMRTFiltersChange } = useGridFilters(GridFilterStorage.entities)

  // Grid pagination with localStorage persistence
  const { pagination, handlePaginationChange } = useGridPagination(GridPaginationStorage.entities)

  // Grid column visibility with localStorage persistence
  const { columnVisibility, handleColumnVisibilityChange } = useGridColumnVisibility(
    GridColumnVisibilityStorage.entities,
    [] // No permanently hidden columns for entities
  )

  const kpiData = getKPIData(entities, setFilteredEntities)

  // One request paints the grid, the KPI strip and the page's search.
  useEffect(() => {
    dispatch(loadEntityScreen() as any)
  }, [])

  // A write the rows could not be patched for: reload once, while on screen.
  useEffect(() => {
    if (stale && !screenLoading) dispatch(loadEntityScreen() as any)
  }, [stale, screenLoading])


  useEffect(() => {
    setFilteredEntities(entities)
  }, [entities])

  // The read named the type and profiles; the grid's display rules (N/A,
  // Multiple, parent else fund manager) apply here, to read and patched rows alike.
  const cleanEntitesData = (entities: EntityScreenRow[]): EntityColsTableDefs[] => {
    return entities.map(entity => ({
      entityName: entity.entityName,
      internalId: entity.internalId,
      status: entity.status === 'Active' ? StatusState.Active : StatusState.Inactive,
      mei: entity.mei,
      kycType: entity.kycType || 'N/A',
      entityType: entityTypeLabel(entity),
      entityProfile: entityProfileLabel(entity),
      parent: entityParentLabel(entity),
      taxFormStatus: entity.taxFormStatus,
      kycStatus: entity.kycStatus,
      id: entity.id,
      original: entity,
    }))
  }

  /**
   * The bulk endpoints address an entity by its id, on the row. They answer
   * per entity; the rows the ledger accepted flip in place, rather than being
   * refetched while PQS may still be projecting them.
   */
  const runBulk = async (
    action: (entities: { id: string }[]) => any,
    status: EntityScreenRow['status'],
    title: string,
  ) => {
    setBulkRunning(true)
    try {
      // the endpoints address an entity by its id alone (the key admin comes
      // from the session); the table hands back its display rows, so send ids
      const result: any = await dispatch(
        action(selectedBulkEntities.map(({ id }) => ({ id }))) as any,
      )
      if (!result?.success) {
        ErrorNotification({ title: `${title} Failed`, message: result?.payload || `${title} failed` })
        return
      }
      const answers: any[] = Array.isArray(result.payload) ? result.payload : []
      const accepted = answers
        .filter((response: any) => response?.id && !response.ErrorMessage && !response.error)
        .map((response: any) => response.id as string)
      dispatch(setEntityScreenStatuses({ ids: accepted, status }))
      // The answer says per entity whether the ledger took it: tell the user,
      // rather than claim success and ask them to look for failures.
      const verb = title === 'Bulk Activate' ? 'Activated' : 'Deactivated'
      const sent = selectedBulkEntities.length
      const reason = answers.find((response: any) => response?.ErrorMessage)?.ErrorMessage
      if (accepted.length === 0) {
        ErrorNotification({ title: `${title} Failed`, message: reason || 'No entity was updated' })
        return
      }
      if (accepted.length < sent) {
        ErrorNotification({
          title: `${title} Partially Failed`,
          message:
            `${accepted.length} of ${sent} entities ${verb.toLowerCase()}; ${sent - accepted.length} refused` +
            (reason ? `: ${reason}` : ''),
        })
        return
      }
      SuccessNotification({
        title: `Successfully ${verb} Bulk Entities`,
        message: `${accepted.length} ${accepted.length === 1 ? 'entity' : 'entities'} ${verb.toLowerCase()}`,
      })
    } catch (e: any) {
      // the thunk's own error handler throws on an answer with no response
      // (a network error or a timeout)
      ErrorNotification({ title: `${title} Failed`, message: e?.message || `${title} failed` })
    } finally {
      setBulkRunning(false)
    }
  }

  const bulkEntityActions = {
    'Activate Entities': () => runBulk(activateBulkEntities, 'Active', 'Bulk Activate'),
    'Deactivate Entities': () => runBulk(deactivateBulkEntities, 'Inactive', 'Bulk Deactivate'),
    'Merge Entities': async () => {
      ErrorNotification({
        title: 'Merge Entities',
        message: 'This action is not yet implemented.',
      })
    }
  }

  const handleRowSelection = (selectedRows: EntityScreenRow[]) => {
    setSelectedBulkEntities(selectedRows)
  }

  return (
    <Stack>
      <KPI_Section kpiData={kpiData} isLoading={globalLoading} />
      <Stack className='optionalTableHeader bulkActions'>
        <Group w="100%">
          <BulkActionsButton selectedItems={selectedBulkEntities} clearSelectedItems={() => {
            setSelectedBulkEntities([]);
            setTableSelectedRows({}); // clear the table selection state
          }} actions={bulkEntityActions} />
        </Group>
      </Stack>
      <MainTable
        tableName='Entities Table'
        isLoading={globalLoading}
        csvExportEnabled={true}
        columnDefs={entityCols}
        enableTopToolbar={false}
        enableRowSelection={true}
        enableMultiRowSelection={true}
        headerBackgroundColor='#F0EEEE'
        tableSelectedRows={tableSelectedRows}
        setTableSelectedRows={setTableSelectedRows}
        data={cleanEntitesData(filteredEntities)}
        initialSorting={[{ id: sorting.sortBy, desc: sorting.order === 'desc' }]}
        onSortingChange={handleSortingChange}
        initialColumnFilters={toMRTColumnFilters()}
        onColumnFiltersChange={handleMRTFiltersChange}
        initialPagination={{ pageSize: pagination.pageSize, pageIndex: pagination.pageIndex }}
        onPaginationChange={handlePaginationChange}
        initialColumnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        setRow={(e: any, cellName: string) => {
          if (cellName != 'id') {
            navigate('/entitymanagement/entity/' + e.id + '/' + e.status)
          }
        }}
        selectedRows={handleRowSelection}
      />
    </Stack>
  )
}
